import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Store,
  Sparkles,
  CheckCircle,
  Key,
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
} from "lucide-react";
import { authService } from "@/services";

/**
 * Página de restablecimiento de contraseña
 *
 * Permite a los usuarios crear una nueva contraseña
 * usando el token de recuperación recibido por email.
 * Optimizada para dispositivos móviles con controles táctiles.
 */
const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);

  // Validar token al cargar la página
  useEffect(() => {
    if (!token) {
      setError("Token de restablecimiento no válido");
      setIsValidToken(false);
      return;
    }

    // Por ahora, asumimos que el token es válido
    // En una implementación real, podrías validar el token con el backend
    setIsValidToken(true);
  }, [token]);

  /**
   * Valida la contraseña según los requisitos
   */
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];

    if (password.length < 6) {
      errors.push("Mínimo 6 caracteres");
    }

    if (!/[A-Za-z]/.test(password)) {
      errors.push("Al menos una letra");
    }

    if (!/\d/.test(password)) {
      errors.push("Al menos un número");
    }

    return errors;
  };

  /**
   * Maneja el envío del formulario de restablecimiento
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading || !token) return;

    // Validaciones
    if (!newPassword.trim()) {
      setError("Por favor, ingresa la nueva contraseña");
      return;
    }

    if (!confirmPassword.trim()) {
      setError("Por favor, confirma la nueva contraseña");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setError(`La contraseña debe tener: ${passwordErrors.join(", ")}`);
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await authService.resetPassword(token, newPassword);
      setSuccess(true);
      
      // Redirigir al login después de 3 segundos
      setTimeout(() => {
        navigate("/login", { 
          state: { 
            message: "Contraseña restablecida exitosamente. Por favor, inicia sesión con tu nueva contraseña." 
          } 
        });
      }, 3000);
    } catch (error: any) {
      setError(error.message || "Error al restablecer la contraseña");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Limpia el error cuando el usuario empieza a escribir
   */
  const handleInputChange = (field: "newPassword" | "confirmPassword", value: string) => {
    if (error) {
      setError("");
    }

    if (field === "newPassword") {
      setNewPassword(value);
    } else {
      setConfirmPassword(value);
    }
  };

  // Si no hay token válido, mostrar error
  if (isValidToken === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-red-100 rounded-full w-fit">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-red-600">Enlace Inválido</CardTitle>
            <CardDescription>
              El enlace de restablecimiento no es válido o ha expirado.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Solicita un nuevo enlace de recuperación desde la página de inicio de sesión.
            </p>
            <Button
              onClick={() => navigate("/forgot-password")}
              className="w-full"
            >
              Solicitar Nuevo Enlace
            </Button>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver al inicio de sesión
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Si está cargando la validación del token
  if (isValidToken === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-sm text-muted-foreground">Validando enlace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Contenido principal - Layout de 2 columnas */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            
            {/* Columna Izquierda - Formulario */}
            <div className="flex flex-col justify-center">
              {/* Hero Section */}
              <div className="text-center lg:text-left mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30 px-2 py-1 rounded-full mb-2 text-xs">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />
                  Sistema de Gestión Escolar
                </Badge>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Restablecer Contraseña
                </h1>
                <p className="text-sm text-muted-foreground">
                  Crea una nueva contraseña segura para tu cuenta
                </p>
              </div>

              {/* Card de Restablecimiento */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardHeader className="space-y-1 pb-3">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="p-1.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg border border-primary/20">
                      <Lock className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-center lg:text-left text-foreground">
                    Nueva Contraseña
                  </CardTitle>
                  <CardDescription className="text-center lg:text-left text-muted-foreground text-xs">
                    Ingresa tu nueva contraseña siguiendo los requisitos de seguridad
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3">
                  {!success ? (
                    <form onSubmit={handleSubmit} className="space-y-3">
                      {/* Campo Nueva Contraseña */}
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="newPassword"
                          className="text-sm font-medium text-foreground"
                        >
                          Nueva Contraseña
                        </Label>
                        <div className="relative">
                          <Input
                            id="newPassword"
                            type={showPasswords.newPassword ? "text" : "password"}
                            placeholder="Ingresa tu nueva contraseña"
                            value={newPassword}
                            onChange={(e) => handleInputChange("newPassword", e.target.value)}
                            disabled={isLoading}
                            required
                            className="h-9 text-sm pr-9 rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                            autoComplete="new-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-1.5 py-1 hover:bg-transparent focus:bg-transparent"
                            onClick={() => setShowPasswords(prev => ({ ...prev, newPassword: !prev.newPassword }))}
                            disabled={isLoading}
                            aria-label={showPasswords.newPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            {showPasswords.newPassword ? (
                              <EyeOff className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Eye className="h-3 w-3 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Campo Confirmar Contraseña */}
                      <div className="space-y-1.5">
                        <Label
                          htmlFor="confirmPassword"
                          className="text-sm font-medium text-foreground"
                        >
                          Confirmar Contraseña
                        </Label>
                        <div className="relative">
                          <Input
                            id="confirmPassword"
                            type={showPasswords.confirmPassword ? "text" : "password"}
                            placeholder="Confirma tu nueva contraseña"
                            value={confirmPassword}
                            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                            disabled={isLoading}
                            required
                            className="h-9 text-sm pr-9 rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                            autoComplete="new-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-1.5 py-1 hover:bg-transparent focus:bg-transparent"
                            onClick={() => setShowPasswords(prev => ({ ...prev, confirmPassword: !prev.confirmPassword }))}
                            disabled={isLoading}
                            aria-label={showPasswords.confirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            {showPasswords.confirmPassword ? (
                              <EyeOff className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <Eye className="h-3 w-3 text-muted-foreground" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Mensaje de Error */}
                      {error && (
                        <Alert
                          variant="destructive"
                          className="border-destructive/50"
                        >
                          <AlertDescription className="text-destructive-foreground">
                            {error}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Botón de Restablecimiento */}
                      <Button
                        type="submit"
                        className="w-full h-9 text-xs font-medium rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Restableciendo contraseña...
                          </>
                        ) : (
                          <>
                            <Lock className="mr-2 h-3 w-3" />
                            Restablecer Contraseña
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    /* Mensaje de Éxito */
                    <div className="space-y-3">
                      <Alert className="border-green-200 bg-green-50/50">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <AlertDescription className="text-green-800">
                          ¡Contraseña restablecida exitosamente! Serás redirigido al inicio de sesión en unos segundos.
                        </AlertDescription>
                      </Alert>
                    </div>
                  )}

                  {/* Enlaces de Navegación */}
                  <div className="text-center lg:text-left pt-1">
                    <p className="text-xs text-muted-foreground">
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <ArrowLeft className="h-3 w-3" />
                        Volver al inicio de sesión
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha - Información de Ayuda */}
            <div className="flex flex-col justify-center space-y-3">
              
              {/* Sección de Requisitos */}
              <div className="space-y-3">
                <div className="text-center lg:text-left">
                  <h2 className="text-lg lg:text-xl font-bold text-foreground mb-1">
                    Requisitos de Seguridad
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Tu nueva contraseña debe cumplir con estos requisitos
                  </p>
                </div>

                {/* Cards de Requisitos */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  <div className="p-2 bg-gradient-to-r from-primary/10 to-primary/5 rounded-md border border-primary/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-primary/20 rounded-sm">
                        <Key className="h-3 w-3 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Mínimo 6 Caracteres
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          La contraseña debe tener al menos 6 caracteres
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-md border border-green-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-green-500/20 rounded-sm">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Al Menos una Letra
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Incluye al menos una letra (A-Z, a-z)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-md border border-blue-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-blue-500/20 rounded-sm">
                        <Clock className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Al Menos un Número
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Incluye al menos un dígito (0-9)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-md border border-purple-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-purple-500/20 rounded-sm">
                        <Zap className="h-3 w-3 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Confirmación Requerida
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Debes confirmar la contraseña para continuar
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de Seguridad */}
              <div className="p-3 bg-gradient-to-r from-muted/50 to-muted/30 rounded-md border border-border/50">
                <h3 className="font-semibold text-xs text-foreground mb-2 flex items-center gap-1">
                  <Shield className="h-2.5 w-2.5 text-primary" />
                  🔒 Seguridad del Enlace
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">•</span>
                    <span>Este enlace es válido por 1 hora</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">•</span>
                    <span>Solo puede ser usado una vez</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">•</span>
                    <span>Tu sesión será cerrada automáticamente</span>
                  </div>
                  <div className="flex items-start gap-2 p-1 bg-background/50 rounded-sm">
                    <span className="font-medium text-primary">•</span>
                    <span>Deberás iniciar sesión nuevamente</span>
                  </div>
                </div>
              </div>

              {/* Sección de Consejos */}
              <div className="p-2 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-md border border-amber-500/20">
                <h4 className="font-medium text-xs mb-1 text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Consejos de Seguridad
                </h4>
                <ul className="text-xs text-amber-600/80 space-y-0.5">
                  <li>• No uses contraseñas fáciles de adivinar</li>
                  <li>• Evita información personal en tu contraseña</li>
                  <li>• No compartas tu contraseña con nadie</li>
                  <li>• Considera usar un gestor de contraseñas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de regreso al final */}
      <div className="p-4 sm:p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 h-7 hover:bg-primary/10 transition-colors text-xs"
        >
          <ArrowLeft className="h-3 w-3" />
          <Store className="h-3 w-3" />
          Volver a la Tienda
        </Button>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 